import React from "react";
import { AlertCircle, Download, Edit, Eye, FileText, Loader2, Plus, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  ContractAdjustmentView,
  ContractCeilingView,
  canEditContractEconomics,
  canManageContractEvidence,
  canReviewContractAdjustment,
  formatAdjustmentNumber,
  formatCents,
  formatDate,
  formatMoneyValue,
  getAdjustmentStatusLabel,
  getAdjustmentTypeLabel,
  getEvidenceTypeLabel,
  isAuditOnlyContractAdjustment,
  parseMoneyToCents,
  summarizeContractCeiling,
} from "./contractAdjustmentsUtils";
import { ContractAdjustmentWizard } from "./ContractAdjustmentWizard";
import { ContractAdjustmentDecisionDialog } from "./ContractAdjustmentDecisionDialog";

type BillingDocument = {
  id: number;
  name: string;
  documentType: string;
  fileUrl?: string | null;
};

type ContractAdjustmentsSectionProps = {
  installation: any;
  contractDocument?: BillingDocument;
  contractFileInputRef: React.RefObject<HTMLInputElement>;
  onContractFileSelected: (file: File | null) => void;
  contractUploadDisabled: boolean;
  contractUploadAllowed: boolean;
};

const statusBadgeClass: Record<string, string> = {
  draft: "border-gray-200 bg-gray-50 text-gray-700",
  pending_approval: "border-amber-200 bg-amber-50 text-amber-700",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rejected: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-gray-200 bg-gray-50 text-gray-500",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={statusBadgeClass[status] || statusBadgeClass.draft}>
      {getAdjustmentStatusLabel(status)}
    </Badge>
  );
}

function AuditOnlyBadge() {
  return (
    <Badge
      variant="outline"
      className="border-slate-300 bg-slate-50 text-slate-700"
      title="Permanece en el historial de auditoría, pero no afecta al cálculo económico vigente."
    >
      Solo auditoría
    </Badge>
  );
}

function amountForAdjustment(adjustment: ContractAdjustmentView) {
  const cents = parseMoneyToCents(adjustment.amount) ?? 0;
  return formatCents(adjustment.direction === "decrease" ? -cents : cents, { signed: true });
}

function ContractInitialDocumentCard({
  contractDocument,
  contractFileInputRef,
  onContractFileSelected,
  contractUploadDisabled,
  contractUploadAllowed,
}: Omit<ContractAdjustmentsSectionProps, "installation">) {
  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 className="font-medium">Contrato inicial</h4>
          <p className="text-sm text-muted-foreground">
            {contractDocument ? contractDocument.name : "Sin contrato inicial adjunto"}
          </p>
        </div>
        <Badge variant={contractDocument ? "default" : "outline"}>
          {contractDocument ? "Disponible" : "No adjunto"}
        </Badge>
      </div>
      {contractUploadAllowed && (
        <input
          ref={contractFileInputRef}
          type="file"
          className="hidden"
          accept="application/pdf,image/*"
          onChange={e => onContractFileSelected(e.target.files?.[0] || null)}
        />
      )}
      <div className="flex flex-wrap gap-2">
        {contractDocument?.fileUrl ? (
          <>
            <Button size="sm" variant="outline" asChild>
              <a href={contractDocument.fileUrl} target="_blank" rel="noopener noreferrer">
                <Eye className="mr-2 h-4 w-4" />
                Ver contrato
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href={contractDocument.fileUrl} download>
                <Download className="mr-2 h-4 w-4" />
                Descargar contrato
              </a>
            </Button>
            {contractUploadAllowed && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => contractFileInputRef.current?.click()}
                disabled={contractUploadDisabled}
              >
                <Upload className="mr-2 h-4 w-4" />
                {contractUploadDisabled ? "Subiendo..." : "Reemplazar contrato"}
              </Button>
            )}
          </>
        ) : contractUploadAllowed ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => contractFileInputRef.current?.click()}
            disabled={contractUploadDisabled}
          >
            <Upload className="mr-2 h-4 w-4" />
            {contractUploadDisabled ? "Subiendo..." : "Subir contrato"}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">La documentación contractual se gestiona desde el contrato.</p>
        )}
      </div>
    </div>
  );
}

function EvidenceList({ adjustmentId }: { adjustmentId: number }) {
  const evidenceQuery = trpc.contractAdjustments.listEvidence.useQuery(
    { adjustmentId },
    { enabled: true, staleTime: 5 * 60 * 1000 },
  );

  if (evidenceQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando evidencias...
      </div>
    );
  }

  if (evidenceQuery.isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        No se pudieron cargar las evidencias.
      </div>
    );
  }

  const evidence = evidenceQuery.data || [];
  if (evidence.length === 0) {
    return <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Sin evidencias vinculadas.</div>;
  }

  return (
    <div className="space-y-2">
      {evidence.map((item: { id: number; documentName?: string | null; evidenceType: string }) => (
        <div key={item.id} className="flex flex-col gap-1 rounded-md border p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="font-medium break-words [overflow-wrap:anywhere]">{item.documentName || "Documento sin nombre"}</div>
            <div className="text-xs text-muted-foreground">{getEvidenceTypeLabel(item.evidenceType)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdjustmentDetail({ adjustment }: { adjustment: ContractAdjustmentView }) {
  return (
    <div className="grid gap-3 text-sm md:grid-cols-2">
      <Detail label="Nº" value={formatAdjustmentNumber(adjustment.adjustmentNumber)} />
      <Detail label="Referencia" value={adjustment.reference || "—"} />
      <Detail label="Tipo" value={getAdjustmentTypeLabel(adjustment.adjustmentType)} />
      <Detail label="Operación" value={adjustment.direction === "decrease" ? "Reducir" : "Incrementar"} />
      <Detail label="Importe" value={amountForAdjustment(adjustment)} />
      <Detail label="Estado" value={getAdjustmentStatusLabel(adjustment.status)} />
      {isAuditOnlyContractAdjustment(adjustment) && (
        <Detail label="Aplicación económica" value="Solo auditoría; no afecta al cálculo vigente" />
      )}
      <Detail label="Fecha efectiva" value={formatDate(adjustment.effectiveDate)} />
      <Detail label="Creación" value={formatDate(adjustment.createdAt)} />
      <div className="md:col-span-2">
        <Detail label="Título" value={adjustment.title} />
      </div>
      <div className="md:col-span-2">
        <Detail label="Motivo" value={adjustment.reason || "—"} />
      </div>
      <Detail label="Enviado" value={formatDate(adjustment.submittedAt)} />
      <Detail label="Aprobado" value={formatDate(adjustment.approvedAt)} />
      <Detail label="Rechazado" value={formatDate(adjustment.rejectedAt)} />
      <Detail label="Cancelado" value={formatDate(adjustment.cancelledAt)} />
      {adjustment.rejectionReason && <Detail label="Motivo rechazo" value={adjustment.rejectionReason} />}
      {adjustment.cancellationReason && <Detail label="Motivo cancelación" value={adjustment.cancellationReason} />}
      <Detail label="Techo anterior" value={adjustment.previousCeilingSnapshot ? formatMoneyValue(adjustment.previousCeilingSnapshot) : "—"} />
      <Detail label="Techo resultante" value={adjustment.resultingCeilingSnapshot ? formatMoneyValue(adjustment.resultingCeilingSnapshot) : "—"} />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 break-words [overflow-wrap:anywhere]">{value}</div>
    </div>
  );
}

export function ContractAdjustmentsSection({
  installation,
  contractDocument,
  contractFileInputRef,
  onContractFileSelected,
  contractUploadDisabled,
  contractUploadAllowed,
}: ContractAdjustmentsSectionProps) {
  const { user } = useAuth();
  const [selectedAdjustment, setSelectedAdjustment] = React.useState<ContractAdjustmentView | null>(null);
  const [reviewingAdjustment, setReviewingAdjustment] = React.useState<ContractAdjustmentView | null>(null);
  const [expandedEvidence, setExpandedEvidence] = React.useState<Record<number, boolean>>({});
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [editingAdjustment, setEditingAdjustment] = React.useState<ContractAdjustmentView | null>(null);

  const adjustmentsQuery = trpc.contractAdjustments.listByInstallation.useQuery({ installationId: installation.id });
  const ceilingQuery = trpc.contractAdjustments.getEffectiveContractCeiling.useQuery({ installationId: installation.id });

  if (adjustmentsQuery.isLoading || ceilingQuery.isLoading) {
    return (
      <Card id="contract-adjustments">
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando información contractual...
        </CardContent>
      </Card>
    );
  }

  if (adjustmentsQuery.isError || ceilingQuery.isError) {
    return (
      <Card id="contract-adjustments">
        <CardContent className="space-y-3 py-6">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <span>No se pudo cargar la información contractual.</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              adjustmentsQuery.refetch();
              ceilingQuery.refetch();
            }}
          >
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  const adjustments = (adjustmentsQuery.data || []) as ContractAdjustmentView[];
  const ceiling = (ceilingQuery.data || null) as ContractCeilingView;
  const summary = summarizeContractCeiling(ceiling, installation.installationPrice, adjustments);
  const canReview = canReviewContractAdjustment(user?.role);
  const initialCents = summary.originalCents;
  const canonicalCurrentCents = summary.effectiveContractCeilingCents;
  const derivedCurrentCents = summary.localEffectiveCents;
  const hasCeilingDiscrepancy = Boolean(ceiling && summary.hasMismatch);
  const statusBadges = [
    ["draft", "Borradores"],
    ["pending_approval", "Pendientes"],
    ["approved", "Aprobadas"],
    ["rejected", "Rechazadas"],
    ["cancelled", "Canceladas"],
  ] as const;

  if (hasCeilingDiscrepancy) {
    console.error("Contract ceiling mismatch", {
      installationId: installation.id,
      derivedCurrentCents,
      canonicalCurrentCents,
    });
  }

  return (
    <Card id="contract-adjustments" className="scroll-mt-24">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-primary">
          <FileText className="h-5 w-5" />
          Contrato y modificaciones
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Contrato inicial" value={formatCents(initialCents)} />
          <Metric label="Ampliaciones aprobadas" value={formatCents(summary.approvedIncreaseCents, { signed: true })} />
          <Metric label="Variación aprobada neta" value={formatCents(summary.approvedDeltaCents, { signed: true })} />
          <Metric label="Reducciones aprobadas" value={formatCents(-summary.approvedDecreaseCents, { signed: true })} />
          <Metric label="Importe contractual vigente" value={formatCents(canonicalCurrentCents)} strong />
        </div>

        {hasCeilingDiscrepancy && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            El importe contractual vigente se muestra desde el cálculo canónico del sistema. Se ha detectado una diferencia en el desglose local.
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {statusBadges.map(([status, label]) => {
            const count = summary.statusCounts[status] ?? 0;
            if (count === 0) return null;
            return <Badge key={status} variant="outline">{label}: {count}</Badge>;
          })}
        </div>

        {canEditContractEconomics(user?.role) && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => {
              setEditingAdjustment(null);
              setWizardOpen(true);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Añadir ampliación o anexo
            </Button>
          </div>
        )}

        <ContractInitialDocumentCard
          contractDocument={contractDocument}
          contractFileInputRef={contractFileInputRef}
          onContractFileSelected={onContractFileSelected}
          contractUploadDisabled={contractUploadDisabled}
          contractUploadAllowed={contractUploadAllowed}
        />

        <section className="space-y-3">
          <div>
            <h4 className="font-medium">Modificaciones contractuales</h4>
            <p className="text-sm text-muted-foreground">Lectura de ampliaciones, reducciones y revisiones asociadas al contrato.</p>
          </div>

          {adjustments.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center">
              <div className="font-medium">Sin modificaciones contractuales</div>
              <div className="mt-1 text-sm text-muted-foreground">Esta instalación mantiene únicamente el importe de su contrato inicial.</div>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto rounded-lg border xl:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº</TableHead>
                      <TableHead>Modificación</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Fecha efectiva</TableHead>
                      <TableHead>Importe</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Evidencias</TableHead>
                      <TableHead>Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map(adjustment => (
                      <React.Fragment key={adjustment.id}>
                        <TableRow>
                          <TableCell className="font-medium">{formatAdjustmentNumber(adjustment.adjustmentNumber)}</TableCell>
                          <TableCell>
                            <div className="font-medium">{adjustment.title}</div>
                            {adjustment.reference && <div className="max-w-[200px] truncate text-xs text-muted-foreground">{adjustment.reference}</div>}
                          </TableCell>
                          <TableCell>{getAdjustmentTypeLabel(adjustment.adjustmentType)}</TableCell>
                          <TableCell>{formatDate(adjustment.effectiveDate)}</TableCell>
                          <TableCell className="font-medium">{amountForAdjustment(adjustment)}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <StatusBadge status={adjustment.status} />
                              {isAuditOnlyContractAdjustment(adjustment) && <AuditOnlyBadge />}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedEvidence(prev => ({ ...prev, [adjustment.id]: !prev[adjustment.id] }))}
                              aria-expanded={Boolean(expandedEvidence[adjustment.id])}
                            >
                              Ver evidencias
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => setSelectedAdjustment(adjustment)}>Ver detalle</Button>
                              {canManageContractEvidence(adjustment.status) && canEditContractEconomics(user?.role) && (
                                <Button variant="outline" size="sm" onClick={() => {
                                  setEditingAdjustment(adjustment);
                                  setWizardOpen(true);
                                }}>
                                  {adjustment.status === "draft" ? (
                                    <><Edit className="mr-1 h-3.5 w-3.5" />Continuar</>
                                  ) : (
                                    <><Edit className="mr-1 h-3.5 w-3.5" />Gestionar evidencias</>
                                  )}
                                </Button>
                              )}
                              {adjustment.status === "pending_approval" && canReview && (
                                <Button size="sm" onClick={() => setReviewingAdjustment(adjustment)}>
                                  Revisar solicitud
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedEvidence[adjustment.id] && (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <EvidenceList adjustmentId={adjustment.id} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-3 xl:hidden">
                {adjustments.map(adjustment => (
                  <div key={adjustment.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{formatAdjustmentNumber(adjustment.adjustmentNumber)}</div>
                        <div className="mt-1 break-words font-medium [overflow-wrap:anywhere]">{adjustment.title}</div>
                        {adjustment.reference && <div className="truncate text-xs text-muted-foreground">{adjustment.reference}</div>}
                      </div>
                      <div className="flex flex-wrap justify-end gap-1">
                        <StatusBadge status={adjustment.status} />
                        {isAuditOnlyContractAdjustment(adjustment) && <AuditOnlyBadge />}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm">
                      <div className="flex justify-between gap-3"><span className="text-muted-foreground">Importe</span><span className="font-medium">{amountForAdjustment(adjustment)}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-muted-foreground">Fecha efectiva</span><span>{formatDate(adjustment.effectiveDate)}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-muted-foreground">Tipo</span><span className="text-right">{getAdjustmentTypeLabel(adjustment.adjustmentType)}</span></div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedAdjustment(adjustment)}>Ver detalle</Button>
                      {canManageContractEvidence(adjustment.status) && canEditContractEconomics(user?.role) && (
                        <Button variant="outline" size="sm" onClick={() => {
                          setEditingAdjustment(adjustment);
                          setWizardOpen(true);
                        }}>
                          {adjustment.status === "draft" ? "Continuar borrador" : "Gestionar evidencias"}
                        </Button>
                      )}
                      {adjustment.status === "pending_approval" && canReview && (
                        <Button size="sm" onClick={() => setReviewingAdjustment(adjustment)}>Revisar solicitud</Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedEvidence(prev => ({ ...prev, [adjustment.id]: !prev[adjustment.id] }))}
                        aria-expanded={Boolean(expandedEvidence[adjustment.id])}
                      >
                        Ver evidencias
                      </Button>
                    </div>
                    {expandedEvidence[adjustment.id] && <div className="mt-3"><EvidenceList adjustmentId={adjustment.id} /></div>}
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </CardContent>

      <Dialog open={Boolean(selectedAdjustment)} onOpenChange={open => !open && setSelectedAdjustment(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedAdjustment ? `${formatAdjustmentNumber(selectedAdjustment.adjustmentNumber)} — ${selectedAdjustment.title}` : "Detalle"}</DialogTitle>
            <DialogDescription>Detalle de la modificación contractual en modo lectura.</DialogDescription>
          </DialogHeader>
          {selectedAdjustment && (
            <div className="space-y-4">
              <AdjustmentDetail adjustment={selectedAdjustment} />
              <div className="space-y-2">
                <h4 className="font-medium">Evidencias</h4>
                <EvidenceList adjustmentId={selectedAdjustment.id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ContractAdjustmentDecisionDialog
        open={Boolean(reviewingAdjustment)}
        adjustment={reviewingAdjustment}
        installationId={installation.id}
        installationPrice={installation.installationPrice}
        currentCeilingCents={canonicalCurrentCents}
        ceiling={ceiling}
        onOpenChange={open => !open && setReviewingAdjustment(null)}
        onSuccess={() => {
          adjustmentsQuery.refetch();
          ceilingQuery.refetch();
        }}
      />

      <ContractAdjustmentWizard
        open={wizardOpen}
        onOpenChange={open => {
          setWizardOpen(open);
          if (!open) setEditingAdjustment(null);
        }}
        installationId={installation.id}
        existingAdjustment={editingAdjustment}
        onSuccess={() => {
          adjustmentsQuery.refetch();
          ceilingQuery.refetch();
        }}
        adjustmentsRefetch={() => adjustmentsQuery.refetch()}
        ceilingRefetch={() => ceilingQuery.refetch()}
      />
    </Card>
  );
}

function Metric({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={strong ? "mt-1 text-xl font-bold" : "mt-1 text-lg font-semibold"}>{value}</div>
    </div>
  );
}
